// Type-only imports: this module is pure (no React, no API calls).
import type { UploadedFile } from '../hooks/useFileUpload';
import type { BoxSelection, TableauSelection, SalesforceSelection } from '../hooks/useFocusMode';
import type { ModelType } from '../hooks/useModelSelector';
import type { ReportGeneration } from '../types';

// The request each chat message is sent to the backend with, built from what
// the user has picked. Every way of sending (send, edit, resend, an older
// message sent again, clarification answers) builds it here, so they can't
// drift apart.

// What a message is sent with, read from Chat's state at the moment of
// sending.
export interface ChatSelections {
  selectedSources: Set<string>;
  deepSearch: boolean;
  webSearch: boolean;
  composeMode: boolean;
  atlasDeepInsights: boolean;
  fileUploadMode: boolean;
  uploadedFiles: UploadedFile[];
  boxSelection: BoxSelection;
  tableauSelection: TableauSelection;
  salesforceSelection: SalesforceSelection;
  salesforceOptions: Record<string, string>;
  // Each picker's selected model, and which pickers are showing
  // (useModelSelector's getModelPickerTypes). A model is sent only while its
  // picker shows.
  selectedModelIds: Record<ModelType, string | null>;
  modelPickerTypes: ModelType[];
  isNewChat: boolean;
}

// The turn itself: which chat, which turn, and what was asked.
export interface ChatTurn {
  sessionId: string;
  turnId: string;
  query: string;
  // Only when answering the agent's clarification questions: one answer per
  // question.
  clarificationAnswers?: string[];
  // Only when sending the "+ → Reports" form.
  reportGeneration?: ReportGeneration;
}

// ── Focus + source fields ───────────────────────────────────────────────────
// Always returns the same key set (focus_dashboard_id/name, focus_salesforce_table
// included as null when not applicable) so the payload shape is predictable
// instead of keys appearing/disappearing based on state.
export const buildFocusFields = ({
  selectedSources,
  composeMode,
  fileUploadMode,
  uploadedFiles,
  boxSelection,
  tableauSelection,
  salesforceSelection,
  salesforceOptions,
}: ChatSelections) => {
  // sources: every combinable source currently selected, excluding Atlas
  // (sent separately as its own standalone atlas_chat object) and Create
  // Image (sent only as its own image_generation flag), plus
  // 'user-uploads' when File Upload is active and 'email_drafter' when
  // Compose is on (the backend's drafting mode keys off that value). Falls
  // back to ['all'] when nothing combinable is selected at all.
  const payloadSources = Array.from(selectedSources).filter((s) => s !== 'atlas' && s !== 'image_generation');
  if (fileUploadMode) payloadSources.push('user-uploads');
  if (composeMode) payloadSources.push('email_drafter');

  const isBoxSelected = selectedSources.has('box');
  const isTableauSelected = selectedSources.has('tableau') && tableauSelection.type === 'dashboard';
  const isSalesforceSelected = selectedSources.has('salesforce') && salesforceSelection.type === 'item';
  // The freshly loaded list wins when there is one; otherwise the value
  // saved with the pick — all a pick restored for a reopened chat has,
  // since the list only loads when a picker opens.
  const salesforceValue = isSalesforceSelected && salesforceSelection.id
    ? salesforceOptions[salesforceSelection.id] ?? salesforceSelection.value ?? null
    : null;

  // Box and uploaded file ids go in separate fields — the backend's Box
  // search filters on box_file_ids/box_folder_ids only, and its upload search
  // and image lookup use user_upload_file_ids only. boxSelection.type
  // 'none'/'all' both mean "no specific folder/file", so both Box lists stay
  // empty in that case.
  const userUploadFileIds = fileUploadMode
    ? uploadedFiles.filter((f) => f.status === 'done').map((f) => f.fileId)
    : [];

  return {
    sources: payloadSources.length > 0 ? payloadSources : ['all'],
    focus_mode: isBoxSelected || selectedSources.has('tableau') || selectedSources.has('salesforce'),
    // Box
    box_file_ids: isBoxSelected && boxSelection.type === 'file' && boxSelection.id
      ? [boxSelection.id] : [],
    box_folder_ids: isBoxSelected && boxSelection.type === 'folder' && boxSelection.id
      ? [boxSelection.id] : [],
    // File Upload
    user_upload_file_ids: userUploadFileIds,
    // Tableau
    focus_dashboard_id: isTableauSelected ? tableauSelection.id : null,
    focus_dashboard_name: isTableauSelected ? tableauSelection.name : null,
    // Salesforce
    focus_salesforce_table: salesforceValue,
  };
};

// ── Model fields ────────────────────────────────────────────────────────────
// Each model is sent only while its picker is showing, null otherwise:
// model (chat) without Create Image, image_model with Create Image on its
// own, and both when Create Image is combined with other selections.
export const buildModelFields = ({ selectedModelIds, modelPickerTypes }: ChatSelections) => ({
  model: modelPickerTypes.includes('chat') ? selectedModelIds.chat : null,
  image_model: modelPickerTypes.includes('image') ? selectedModelIds.image : null,
});

// ── The whole request ───────────────────────────────────────────────────────
export const buildChatPayload = (selections: ChatSelections, turn: ChatTurn) => {
  // atlas_chat payload shape: enabled + which mode is active. atlasDeepInsights
  // only ever matters while Atlas is selected — the hook resets it to false
  // whenever Atlas is deselected, so 'normal' is always the default the next
  // time Atlas gets picked.
  const isAtlasSelected = selections.selectedSources.has('atlas');

  return {
    session_id: turn.sessionId,
    turn_id: turn.turnId,
    query: turn.query,
    ...(turn.clarificationAnswers ? { clarification_answers: turn.clarificationAnswers } : {}),
    // The report form's { report_id, values }; null on every other message.
    report_generation: turn.reportGeneration ?? null,
    deep_research: selections.deepSearch,
    image_generation: selections.selectedSources.has('image_generation'),
    web_search: selections.webSearch,
    atlas_chat: {
      enabled: isAtlasSelected,
      modes: {
        normal: isAtlasSelected && !selections.atlasDeepInsights,
        deep_insights: isAtlasSelected && selections.atlasDeepInsights,
      },
    },
    ...buildModelFields(selections),
    // Stays true for every message in a new conversation. Only false when the
    // user resumed from history (urlSessionId was in the URL on mount).
    is_new_chat: selections.isNewChat,
    ...buildFocusFields(selections),
  };
};
